import ClickAwayListener from '@mui/material/ClickAwayListener';
import type { PaperProps } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import { AppCustomMenu } from '@lmt/unicorn-shared/app/components/common/ui-kit/menu/AppCustomMenu';
import { MenuDirection } from '@lmt/unicorn-shared/app/components/common/ui-kit/menu/MenuDirection';
import {
  IconButton,
  type IconButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import optionListStyles from '../OptionList.module.scss';
import { ButtonColor } from './Button';

export type OptionProps = {
  displayValue: string;
  value: string | number;
  color?: ButtonColor;
  onClick?: () => void;
  testId?: string;
};

interface IconDropDownButtonProps {
  iconComponent: React.ReactNode;
  color: IconButtonColor;
  disabled?: boolean;
  options: OptionProps[];
  onOptionClick?: (value: OptionProps) => void;
  optionListClassName?: string;
  className?: string;
  testId?: string;
  menuDirection?: MenuDirection;
}

export const IconDropDownButton: React.FC<IconDropDownButtonProps> = (
  props,
) => {
  const {
    iconComponent,
    color,
    disabled,
    options,
    onOptionClick,
    optionListClassName,
    className,
    testId,
    menuDirection,
  } = props;

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const expanded = Boolean(anchorEl);
  const hide = useCallback(() => setAnchorEl(null), []);

  const paperSlotProps: PaperProps & { 'data-test-id': string } = {
    className: optionListClassName,
    sx: { pointerEvents: expanded ? 'auto' : 'none' },
    'data-test-id': 'icon-drop-down-option-list',
  };

  const onIconClickHandler = useCallback((event: React.MouseEvent<Element>) => {
    setAnchorEl((oldAnchorEl) => {
      return oldAnchorEl ? null : event.currentTarget;
    });
  }, []);

  return (
    <ClickAwayListener
      onClickAway={hide}
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
    >
      <div>
        <IconButton
          iconComponent={iconComponent}
          color={color}
          disabled={disabled}
          onClick={onIconClickHandler}
          enableClickPropagation={true}
          testId={testId ?? 'icon-drop-down'}
          className={className}
          expanded={expanded}
        />

        <AppCustomMenu
          anchorEl={anchorEl}
          open={expanded}
          onClose={hide}
          direction={menuDirection ?? MenuDirection.bottomRightEdge}
          closeOnClick={true}
          hideBackdrop={true}
          slotProps={{
            root: {
              sx: { pointerEvents: 'none' },
            },
            paper: paperSlotProps,
          }}
        >
          {options.length === 0 ? (
            <div className={optionListStyles.optionContainer}>
              <div className={optionListStyles.selectedValue}>{'No data'}</div>
            </div>
          ) : (
            options.map((option) => (
              <div
                key={`${option.displayValue}`}
                className={optionListStyles.optionContainer}
                onClick={() => {
                  flushSync(hide);
                  option.onClick?.();
                  onOptionClick?.(option);
                }}
                data-test-id={'icon-drop-down-option-item'}
              >
                <div
                  data-color={option.color ?? ''}
                  className={optionListStyles.selectedValue}
                >
                  {option.displayValue}
                </div>
              </div>
            ))
          )}
        </AppCustomMenu>
      </div>
    </ClickAwayListener>
  );
};
